import React, { useMemo, useState } from 'react';
import { apiFetch } from '../../../../lib/api';
import { showToast } from '../../../../components/Toast';
import ApiInspector from '../../../../components/ApiInspector';
import { formatCurrency, formatQuantity, formatNumber } from '../../../../utils/formatters';
import { API_DOCS } from './ModalContrattualizzazioneRiga.api';

// ── Tipi ──────────────────────────────────────────────────────────────────
// Sottoinsieme della RigaOrdine (definita in SchedaOrdine.tsx) usato dal modale.
interface RigaOrdine {
  id: number;
  ordinePos: number;
  articoloId: number | null;
  articolo?: { id: number; codice: string; descrizioneBase: string; tipologia?: string } | null;
  descrizione: string;
  descrizioneEstesa: string | null;
  quantita: string | null;
  prezzoUnitario: string | null;
  importoNetto: string | null;
  periodicita: string | null;
  dataInizio: string | null;
  dataFine: string | null;
  modalitaPagamento: string | null;
  tariffaUnitaria: string | null;
  tariffaFissaTransazione: string | null;
  percentualeTransazione: string | null;
}

interface Props {
  ordineId: number;
  societaId: number; // coerenza con gli altri modali (non inviato nel body)
  riga: RigaOrdine; // riga RICORRENTE sorgente
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

type Periodicita = 'MENSILE' | 'TRIMESTRALE' | 'SEMESTRALE' | 'ANNUALE';
type ModalitaPagamento = 'A_PREVENTIVO' | 'A_CONSUNTIVO_VOLUMI' | 'A_CONSUNTIVO_TRANSAZIONI';

const PERIODICITA_OPZIONI: { value: Periodicita; label: string }[] = [
  { value: 'MENSILE', label: 'Mensile' },
  { value: 'TRIMESTRALE', label: 'Trimestrale' },
  { value: 'SEMESTRALE', label: 'Semestrale' },
  { value: 'ANNUALE', label: 'Annuale' },
];

const MODALITA_OPZIONI: { value: ModalitaPagamento; label: string }[] = [
  { value: 'A_PREVENTIVO', label: 'A preventivo' },
  { value: 'A_CONSUNTIVO_VOLUMI', label: 'A consuntivo (volumi)' },
  { value: 'A_CONSUNTIVO_TRANSAZIONI', label: 'A consuntivo (transazioni)' },
];

// Converte una data ISO (o yyyy-MM-dd) nel formato yyyy-MM-dd per <input type="date">.
function toInputDate(value: string | null): string {
  if (!value) return '';
  return value.length >= 10 ? value.slice(0, 10) : value;
}

function isNumStr(value: string | null): boolean {
  return value !== null && value !== '' && Number.isFinite(Number(value));
}

// ── Componente principale ─────────────────────────────────────────────────
const ModalContrattualizzazioneRiga: React.FC<Props> = ({
  ordineId,
  societaId,
  riga,
  onClose,
  onSaved,
}) => {
  const [dataInizio, setDataInizio] = useState<string>(() => toInputDate(riga.dataInizio));
  const [dataFine, setDataFine] = useState<string>(() => toInputDate(riga.dataFine));
  const [periodicita, setPeriodicita] = useState<Periodicita>(
    () => (riga.periodicita as Periodicita) || 'MENSILE',
  );
  const [modalitaPagamento, setModalitaPagamento] = useState<ModalitaPagamento>(
    () => (riga.modalitaPagamento as ModalitaPagamento) || 'A_PREVENTIVO',
  );
  const [descrizione, setDescrizione] = useState<string>(
    () => riga.descrizioneEstesa || riga.descrizione || '',
  );

  const [salvando, setSalvando] = useState(false);
  const [erroreServer, setErroreServer] = useState<string | null>(null);

  // ── Validazione client ───────────────────────────────────────────────────
  const dateInvertite = useMemo(() => {
    if (!dataInizio || !dataFine) return false;
    return dataFine < dataInizio;
  }, [dataInizio, dataFine]);

  const requiredOk =
    dataInizio.trim() !== '' && dataFine.trim() !== '' && descrizione.trim() !== '';
  const confermaAbilitata = requiredOk && !salvando;

  // ── Submit ───────────────────────────────────────────────────────────────
  const salva = async () => {
    if (!confermaAbilitata) return;
    setSalvando(true);
    setErroreServer(null);
    try {
      // @api POST /api/ordini/:id/righe/:rigaId/contrattualizzazione {"dataInizio":"string","dataFine":"string","periodicita":"string","modalitaPagamento":"string","descrizione":"string"} -> {"success":true,"data":{"contrattoId":"number","numero":"string"}}
      const res = await apiFetch<{
        success: boolean;
        data?: { contrattoId: number; numero: string };
        error?: string | { code?: string; message?: string };
      }>(`/api/ordini/${ordineId}/righe/${riga.id}/contrattualizzazione`, {
        method: 'POST',
        body: JSON.stringify({
          dataInizio,
          dataFine,
          periodicita,
          modalitaPagamento,
          descrizione: descrizione.trim(),
        }),
      });

      if (!res.success) {
        const err = res.error;
        const msg =
          (err && typeof err === 'object' ? err.message : (err as string)) ||
          'Errore contrattualizzazione';
        setErroreServer(msg);
        return;
      }

      showToast(`Contratto ${res.data?.numero} creato`, 'success');
      await onSaved();
    } catch (e) {
      // apiFetch lancia errori con .payload contenente il body parsato.
      const err = e as {
        message?: string;
        payload?: { error?: string | { code?: string; message?: string } };
      };
      const apiErr = err?.payload?.error;
      if (apiErr && typeof apiErr === 'object') {
        setErroreServer(apiErr.message || 'Errore contrattualizzazione');
      } else if (typeof apiErr === 'string') {
        setErroreServer(apiErr);
      } else {
        setErroreServer(err?.message || 'Errore contrattualizzazione');
      }
    } finally {
      setSalvando(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="bop-scheda-ordine__esplosione-overlay">
      <div className="bop-scheda-ordine__esplosione-box bop-modal-contrattualizza__box">
        <div className="bop-scheda-ordine__esplosione-header">
          <h2 className="bop-scheda-ordine__esplosione-title">Contrattualizzazione riga</h2>
          <button
            type="button"
            className="bop-btn-icon bop-scheda-ordine__esplosione-close"
            onClick={onClose}
            aria-label="Chiudi"
            title="Chiudi"
            disabled={salvando}
          >
            ×
          </button>
        </div>

        <div className="bop-scheda-ordine__esplosione-body">
          {/* Intestazione read-only (D89) */}
          <div className="bop-modal-contrattualizza__riga">
            <div className="bop-modal-contrattualizza__riga-head">
              <span className="bop-scheda-ordine__esplosione-articolo-codice">
                {riga.articolo?.codice ?? '—'}
              </span>
              <span className="bop-badge bop-badge-neutral">Riga n° {riga.ordinePos}</span>
            </div>
            <div className="bop-scheda-ordine__esplosione-articolo-desc">
              {riga.articolo?.descrizioneBase ?? riga.descrizione}
            </div>

            <div className="bop-modal-contrattualizza__importi">
              {isNumStr(riga.quantita) && (
                <div className="bop-modal-contrattualizza__importo">
                  <span className="bop-text-muted">Quantità</span>
                  <strong>{formatQuantity(Number(riga.quantita))}</strong>
                </div>
              )}
              {isNumStr(riga.prezzoUnitario) && (
                <div className="bop-modal-contrattualizza__importo">
                  <span className="bop-text-muted">Prezzo unitario</span>
                  <strong>{formatCurrency(Number(riga.prezzoUnitario))}</strong>
                </div>
              )}
              {isNumStr(riga.importoNetto) && (
                <div className="bop-modal-contrattualizza__importo">
                  <span className="bop-text-muted">Importo netto</span>
                  <strong>{formatCurrency(Number(riga.importoNetto))}</strong>
                </div>
              )}
              {isNumStr(riga.tariffaUnitaria) && (
                <div className="bop-modal-contrattualizza__importo">
                  <span className="bop-text-muted">Tariffa unitaria</span>
                  <strong>{formatCurrency(Number(riga.tariffaUnitaria))}</strong>
                </div>
              )}
              {isNumStr(riga.tariffaFissaTransazione) && (
                <div className="bop-modal-contrattualizza__importo">
                  <span className="bop-text-muted">Tariffa fissa transazione</span>
                  <strong>{formatCurrency(Number(riga.tariffaFissaTransazione))}</strong>
                </div>
              )}
              {isNumStr(riga.percentualeTransazione) && (
                <div className="bop-modal-contrattualizza__importo">
                  <span className="bop-text-muted">Percentuale transazione</span>
                  <strong>{formatNumber(Number(riga.percentualeTransazione))} %</strong>
                </div>
              )}
            </div>
          </div>

          {/* Campi editabili */}
          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Data inizio</label>
              <input
                type="date"
                className="bop-input"
                value={dataInizio}
                onChange={(e) => setDataInizio(e.target.value)}
                disabled={salvando}
              />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Data fine</label>
              <input
                type="date"
                className="bop-input"
                value={dataFine}
                onChange={(e) => setDataFine(e.target.value)}
                disabled={salvando}
              />
            </div>
          </div>

          {dateInvertite && (
            <div className="bop-text-danger bop-modal-contrattualizza__warning">
              La data fine è precedente alla data inizio.
            </div>
          )}

          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Periodicità di fatturazione</label>
              <select
                className="bop-select"
                value={periodicita}
                onChange={(e) => setPeriodicita(e.target.value as Periodicita)}
                disabled={salvando}
              >
                {PERIODICITA_OPZIONI.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Modalità di determinazione importo</label>
              <select
                className="bop-select"
                value={modalitaPagamento}
                onChange={(e) => setModalitaPagamento(e.target.value as ModalitaPagamento)}
                disabled={salvando}
              >
                {MODALITA_OPZIONI.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Descrizione contratto</label>
            <textarea
              className="bop-input bop-modal-contrattualizza__textarea"
              rows={4}
              value={descrizione}
              onChange={(e) => setDescrizione(e.target.value)}
              disabled={salvando}
            />
          </div>

          {erroreServer && (
            <div className="bop-scheda-ordine__esplosione-errore">
              <strong>Errore:</strong> {erroreServer}
            </div>
          )}
        </div>

        <div className="bop-scheda-ordine__esplosione-footer">
          <button type="button" className="bop-btn-secondary" onClick={onClose} disabled={salvando}>
            Annulla
          </button>
          <button
            type="button"
            className="bop-btn-primary"
            onClick={salva}
            disabled={!confermaAbilitata}
            title={
              !requiredOk
                ? 'Compila date e descrizione per confermare'
                : 'Conferma contrattualizzazione'
            }
          >
            {salvando ? 'Salvataggio…' : 'Conferma'}
          </button>
        </div>
      </div>

      <ApiInspector docs={API_DOCS} />
    </div>
  );
};

export default ModalContrattualizzazioneRiga;
